import { Component, HostListener, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TenantsService } from '../../../services/tenants.service';
import { Tenant } from '../../../model/tenant';
import { TenantPersistRequest } from '../../../model/requests/tenant-persist-request';
import { CountryISO, PhoneNumberFormat, SearchCountryField } from 'ngx-intl-tel-input';
import { PhoneNumberFormat as PNF, PhoneNumberUtil } from 'google-libphonenumber';
import { TranslocoService } from '@ngneat/transloco';
import { ComponentCanDeactivate } from '../../../common/can-deactivate/component-can-deactivate';
import { AppConfig } from '../../../shared/global-variables';
import { noSpecialCharacters, noWhitespaces } from '../../../shared/custom-validators';
import { CommonMessagesService } from '../../../services/common-messages.service';
import * as moment from "moment";

const phoneUtil = PhoneNumberUtil.getInstance();

@Component({
  selector: 'app-tenants-form',
  templateUrl: './tenants-form.component.html',
  styleUrls: ['./tenants-form.component.scss']
})
export class TenantsFormComponent implements OnInit, ComponentCanDeactivate {

  public tenant: Tenant;

  public tenantForm: FormGroup;
  public persistingChanges: any;
  public textFields: string[];

  public separateDialCode = false;
  public SearchCountryField = SearchCountryField;
  public CountryISO = CountryISO;
  public PhoneNumberFormat = PhoneNumberFormat;
  public PhoneNumberUtil = PhoneNumberUtil;
  public preferredCountries: CountryISO[] = [CountryISO.UnitedStates, CountryISO.UnitedKingdom];
  public formSuccess: boolean;

  public fromDate: Date;
  public entryDate: Date;

  public mode: 'create' | 'edit';

  constructor(private formBuilder: FormBuilder,
              private route: ActivatedRoute,
              private router: Router,
              private i18n: TranslocoService,
              private tenantsService: TenantsService,
              private commonMessagesService: CommonMessagesService) {
    this.fromDate = moment().startOf('day').toDate();
  }

  ngOnInit(): void {
    this.route.url.subscribe(urlSegment => {
      const firstPath = urlSegment[0].path;
      if (firstPath === 'new') {
        this.mode = 'create';
        this.loadCreationForm();
      } else {
        this.mode = 'edit';
        this.loadEditionForm(firstPath);
      }
    });
  }

  loadCreationForm(): void {
    this.tenant = new Tenant();
    this.createForm(this.tenant);
  }

  loadEditionForm(uuid): void {
    this.tenantsService.getTenant(uuid).toPromise().then(response => {
      this.tenant = response.tenant;
      this.parsePhoneNumber(this.tenant);
      this.createForm(this.tenant);
    });
  }

  private createForm(tenant: Tenant): void {
    if (!tenant.exchange) {
      tenant.exchange = "default";
    }
    this.tenantForm = this.formBuilder.group({
      name: [tenant.name, [
        Validators.minLength(AppConfig.FORM_IDENTIFIER_MIN_LENGTH),
        Validators.maxLength(255),
        Validators.required,
        noSpecialCharacters,
        noWhitespaces
      ]],
      description: [tenant.description, [Validators.maxLength(255)]],
      email: [tenant.email, [Validators.maxLength(255), Validators.required, Validators.email]],
      phone: [tenant.phone, [Validators.maxLength(255)]],
      address: [tenant.address, [Validators.maxLength(255)]],
      address2: [tenant.address2, [Validators.maxLength(255)]],
      city: [tenant.city, [Validators.maxLength(255)]],
      zip: [tenant.zip, [Validators.maxLength(255)]],
      region: [tenant.region, [Validators.maxLength(255)]],
      country: [tenant.country, [Validators.maxLength(255)]],
      exchange: [tenant.exchange, [Validators.maxLength(255), Validators.required]],
      entryDate: [tenant.entryDate ? new Date(tenant.entryDate) : this.fromDate, [Validators.required]],
      expirationDate: [tenant.expirationDate ? new Date(tenant.expirationDate) : null]
    });
    this.tenantForm.markAsPending();

    this.textFields = [];
    const alreadyDefinedFields = ['name', 'description', 'entryDate', 'expirationDate', 'exchange'];
    const formFields = Object.keys(this.tenantForm.getRawValue());
    for (const field of formFields) {
      if (!alreadyDefinedFields.includes(field)){
        this.textFields.push(field);
      }
    }
  }

  changeEntryDate(): void {
    this.entryDate = this.tenantForm.get('entryDate').value;
    this.tenantForm.get('expirationDate').setValue(null);
  }

  persistTenantData(): void {
    if (!this.tenantForm.valid) { return; }

    this.persistingChanges = true;
    const persistData = this.buildPersistData();

    let promise;
    if (this.mode === 'create') {
      promise = this.tenantsService.createTenant(persistData).toPromise();
    } else {
      promise = this.tenantsService.editTenant(persistData).toPromise();
    }

    promise.then(response => {
      this.formSuccess = true;
      if (this.mode === 'create') {
        this.router.navigate([`/management/tenants/${response.uuid}/details`]);
      } else {
        this.router.navigate([`/management/tenants/${this.tenant.uuid}/details`]);
      }
    }).catch(error => {
      this.commonMessagesService.showPersistingError(this.mode, error, persistData.name);
      this.persistingChanges = false;
    });
  }

  private buildPersistData(): TenantPersistRequest {
    const formValue = this.tenantForm.getRawValue();
    return {
      name: formValue.name,
      description: formValue.description,
      address: formValue.address,
      address2: formValue.address2,
      city: formValue.city,
      country: formValue.country,
      email: formValue.email,
      phone: formValue.phone ? formValue.phone.e164Number : null,
      region: formValue.region,
      zip: formValue.zip,
      exchange: formValue.exchange,
      entryDate: formValue.entryDate.getTime(),
      expirationDate: formValue.expirationDate ? formValue.expirationDate.getTime() : null,
      uuid: this.mode === 'edit' ? this.tenant.uuid : null
    };
  }

  private parsePhoneNumber(tenant: Tenant): void {
    try {
      const rawPhone = tenant.phone;
      const firstNumber = phoneUtil.parse(rawPhone);
      const regionCode = phoneUtil.getRegionCodeForNumber(firstNumber);
      const countryCode = firstNumber.getCountryCode();

      const e164 = phoneUtil.format(firstNumber, PNF.E164);
      const nationalNumber = phoneUtil.format(firstNumber, PNF.National);
      const internationalNumber = phoneUtil.format(firstNumber, PNF.National);

      this.tenant.phone = {
        countryCode: regionCode,
        dialCode: `+${countryCode}`,
        e164Number: e164,
        internationalNumber: `+${countryCode} ${internationalNumber}`,
        nationalNumber,
        number: nationalNumber
      };
    } catch (e) {
      this.tenant.phone = {};
    }
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.formSuccess || this.tenantForm.untouched;
  }

}
